/**
 * Vulnerability radar — Phase 10d visual overlay for the Federation Impact card.
 *
 * Plots six "hard-to-reach" proxies (each as a percentage of the federation's
 * member total) on a hexagonal radar. Makes cohort difficulty shape obvious
 * at a glance — CCHI's profile is visibly different from IFR's or VYC's.
 * Pure-SVG, no chart library dependency.
 */

import React from 'react';

interface FederationRow {
  members_total?: number | null;
  members_non_english?: number | null;
  members_no_email?: number | null;
  members_no_postal?: number | null;
  members_phone_only?: number | null;
  members_unreachable?: number | null;
  members_in_age_cohort?: number | null;
}

interface Props {
  row: FederationRow | null | undefined;
}

const num = (v: unknown): number => {
  if (v == null) return 0;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Each axis carries a short label (for the chart) and a longer description
// (rendered as a title tooltip + below the chart so funders/clients know
// what each acronym means without hover).
const AXES = [
  {
    key: 'lep',
    label: 'LEP',
    desc: 'Limited English Proficiency — primary language is not English',
  },
  {
    key: 'no_email',
    label: 'No email',
    desc: 'No email address on file (digital exclusion)',
  },
  {
    key: 'no_postal',
    label: 'No postal',
    desc: 'Missing city or ZIP — cannot receive mail',
  },
  {
    key: 'phone_only',
    label: 'Phone-only',
    desc: 'Reachable only via phone (no email at all)',
  },
  {
    key: 'unreachable',
    label: 'Unreachable',
    desc: 'Flagged unreachable in legacy contact status',
  },
  {
    key: 'older_adult',
    label: 'Older adult',
    desc: 'Born 1973 or earlier (50+ at program start)',
  },
] as const;

export const VulnerabilityRadarChart: React.FC<Props> = ({ row }) => {
  if (!row) return null;
  const total = num(row.members_total);
  if (total <= 0) {
    return (
      <div className="text-xs text-gray-500">
        No vulnerability data for this federation.
      </div>
    );
  }

  const pctOf = (v: unknown) => Math.min(100, (num(v) / total) * 100);

  const values: Record<string, number> = {
    lep: pctOf(row.members_non_english),
    no_email: pctOf(row.members_no_email),
    no_postal: pctOf(row.members_no_postal),
    phone_only: pctOf(row.members_phone_only),
    unreachable: pctOf(row.members_unreachable),
    older_adult: pctOf(row.members_in_age_cohort),
  };

  // Wider viewBox + smaller radius leaves room for axis labels without
  // clipping. Anchor labels by quadrant so they read cleanly.
  const width = 320;
  const height = 280;
  const cx = width / 2;
  const cy = height / 2;
  const radius = 88;
  const n = AXES.length;

  const angleFor = (i: number) => -Math.PI / 2 + (i * 2 * Math.PI) / n;

  const polarToCart = (theta: number, r: number) => ({
    x: cx + Math.cos(theta) * r,
    y: cy + Math.sin(theta) * r,
  });

  // Concentric grid rings at 25/50/75/100%
  const rings = [0.25, 0.5, 0.75, 1].map((p) => {
    const pts = AXES.map((_, i) => {
      const t = angleFor(i);
      const r = radius * p;
      const c = polarToCart(t, r);
      return `${c.x.toFixed(1)},${c.y.toFixed(1)}`;
    }).join(' ');
    return { p, pts };
  });

  // Value polygon
  const valuePoints = AXES.map((a, i) => {
    const t = angleFor(i);
    const r = radius * (values[a.key] / 100);
    const c = polarToCart(t, r);
    return `${c.x.toFixed(1)},${c.y.toFixed(1)}`;
  }).join(' ');

  const axisInfo = AXES.map((a, i) => {
    const t = angleFor(i);
    const end = polarToCart(t, radius);
    // Push labels further out to clear the polygon
    const labelPos = polarToCart(t, radius + 26);
    return { axis: a, end, labelPos, value: values[a.key], theta: t };
  });

  return (
    <div className="w-full">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-auto"
        role="img"
        aria-label="Vulnerability radar"
      >
        {/* Ring gridlines */}
        {rings.map(({ p, pts }) => (
          <polygon
            key={p}
            points={pts}
            className="fill-none stroke-gray-200"
            strokeWidth={1}
            strokeDasharray={p === 1 ? '0' : '2 3'}
          />
        ))}
        {/* Ring percentage hints (right axis only, less visual noise) */}
        {[0.25, 0.5, 0.75, 1].map((p) => (
          <text
            key={`pct-${p}`}
            x={cx + 2}
            y={cy - radius * p + 3}
            className="text-[8px] fill-gray-400"
          >
            {Math.round(p * 100)}%
          </text>
        ))}
        {/* Axis lines */}
        {axisInfo.map(({ axis, end }) => (
          <line
            key={axis.key}
            x1={cx}
            y1={cy}
            x2={end.x}
            y2={end.y}
            className="stroke-gray-200"
            strokeWidth={1}
          />
        ))}
        {/* Value polygon */}
        <polygon
          points={valuePoints}
          className="fill-rose-300/40 stroke-rose-500"
          strokeWidth={1.5}
        />
        {/* Value dots */}
        {AXES.map((a, i) => {
          const t = angleFor(i);
          const r = radius * (values[a.key] / 100);
          const c = polarToCart(t, r);
          return (
            <circle key={a.key} cx={c.x} cy={c.y} r={2.5} className="fill-rose-600" />
          );
        })}
        {/* Axis labels — anchor by quadrant so they hug their axis cleanly */}
        {axisInfo.map(({ axis, labelPos, value, theta }) => {
          // Horizontal anchor: text-anchor depends on which side of the
          // center the label sits. Vertical: use dominant-baseline for
          // top/bottom labels.
          const cos = Math.cos(theta);
          const sin = Math.sin(theta);
          const anchor: 'start' | 'end' | 'middle' =
            cos < -0.2 ? 'end' : cos > 0.2 ? 'start' : 'middle';
          const baseline =
            sin < -0.5 ? 'auto' : sin > 0.5 ? 'hanging' : 'middle';
          return (
            <g key={axis.key}>
              <title>{axis.desc}</title>
              <text
                x={labelPos.x}
                y={labelPos.y}
                textAnchor={anchor}
                dominantBaseline={baseline}
                className="text-[10px] fill-gray-700 font-medium"
              >
                {axis.label}
              </text>
              <text
                x={labelPos.x}
                y={labelPos.y + (baseline === 'auto' ? -11 : 11)}
                textAnchor={anchor}
                dominantBaseline={baseline}
                className="text-[9px] fill-gray-500 tabular-nums"
              >
                {value.toFixed(0)}%
              </text>
            </g>
          );
        })}
      </svg>

      {/* Legend with full descriptions */}
      <div className="mt-2 grid grid-cols-1 gap-x-3 gap-y-0.5 text-[10px] text-gray-600 sm:grid-cols-2">
        {AXES.map((a) => (
          <div key={a.key} className="flex items-baseline gap-1.5">
            <span className="font-medium text-gray-800 flex-shrink-0">{a.label}:</span>
            <span className="text-gray-500 leading-tight">{a.desc}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default VulnerabilityRadarChart;
